/**
 * Pure helpers for multifile / folder template upload.
 * Kept outside Templates.tsx so duplicate/cap semantics can be unit-tested.
 *
 * Uniqueness contract (must match backend master_templates partial unique index):
 * - Global (not creator-scoped)
 * - Case-insensitive template name
 * - Scoped by modality
 * - Only active rows participate (inactive names may be reused)
 */

export type BulkNameSource = {
  suggestedName: string;
  /** Locked name from a prior attempt — must not be recomputed on partial retry. */
  resolvedFileName?: string;
};

export type UploadDuplicateIdentity = {
  templateName: string;
  modality?: string | null;
};

/** Ensure upload basename meets backend template_name minimum (3 chars). */
export function ensureMinUploadTemplateName(name: string, minLen = 3): string {
  const trimmed = String(name || '').trim() || 'Upload';
  if (trimmed.length >= minLen) {
    return trimmed.slice(0, 255);
  }
  const padded = `${trimmed} Upload`.slice(0, 255);
  return padded.length >= minLen ? padded : 'Upload';
}

/**
 * Assign unique display/persisted file basenames.
 * Rows with `resolvedFileName` keep that exact value so partial retry cannot
 * collapse `Chest (2)` back to `Chest` after `Chest` was already created.
 */
export function uniqueBulkTemplateNames(documents: BulkNameSource[]): string[] {
  const result: string[] = new Array(documents.length);
  const usedLower = new Set<string>();

  documents.forEach((doc, i) => {
    const locked = (doc.resolvedFileName || '').trim();
    if (!locked) {
      return;
    }
    const name = locked.slice(0, 255);
    result[i] = name;
    usedLower.add(name.toLowerCase());
  });

  documents.forEach((doc, i) => {
    if (result[i]) {
      return;
    }
    const base = ensureMinUploadTemplateName((doc.suggestedName || 'Upload').trim() || 'Upload');
    let n = 1;
    let candidate = base.slice(0, 255);
    while (usedLower.has(candidate.toLowerCase())) {
      n += 1;
      const suffix = ` (${n})`;
      candidate = `${base.slice(0, Math.max(1, 255 - suffix.length))}${suffix}`;
    }
    result[i] = candidate;
    usedLower.add(candidate.toLowerCase());
  });

  return result;
}

/** Lock computed unique names onto row objects (idempotent if already resolved). */
export function withResolvedBulkFileNames<T extends BulkNameSource>(
  documents: T[]
): Array<T & { resolvedFileName: string }> {
  const names = uniqueBulkTemplateNames(documents);
  return documents.map((doc, i) => ({
    ...doc,
    resolvedFileName: (doc.resolvedFileName || '').trim() || names[i],
  }));
}

/**
 * Normalize persisted template name for duplicate comparison.
 * Preserves trailing " (n)" so Chest vs Chest (2) stay distinct.
 */
export function normalizeUploadTemplateNameForDuplicate(templateName: string): string {
  return String(templateName || '')
    .trim()
    .toLowerCase();
}

/**
 * Active master-template identity key: case-insensitive name + modality.
 * Matches backend UNIQUE (LOWER(template_name), modality_id) WHERE is_deleted = false.
 */
export function uploadDuplicateKey(
  templateName: string,
  modality?: string | null
): string {
  const name = normalizeUploadTemplateNameForDuplicate(templateName);
  const mod = String(modality || '')
    .trim()
    .toLowerCase();
  return `${name}::${mod}`;
}

/** Within-batch + existing-key duplicate flags (same logic as bulk review preflight). */
export function findWithinBatchDuplicateFlags(
  identities: UploadDuplicateIdentity[],
  existingKeys: Set<string> = new Set()
): boolean[] {
  const flags = identities.map(() => false);
  const firstIndexByKey = new Map<string, number>();

  for (let i = 0; i < identities.length; i += 1) {
    const identity = identities[i];
    const key = uploadDuplicateKey(identity.templateName, identity.modality);
    if (existingKeys.has(key)) {
      flags[i] = true;
    }
    const prev = firstIndexByKey.get(key);
    if (prev != null) {
      flags[prev] = true;
      flags[i] = true;
    } else {
      firstIndexByKey.set(key, i);
    }
  }

  return flags;
}

/**
 * Batch upload (2–5 files): refresh the list when some rows succeeded or were skipped,
 * but keep the modal open while API failures remain.
 */
export function batchUploadShouldRefreshWithoutClose(outcome: {
  failedCount: number;
  failedDocsLength: number;
  createdCount: number;
  skippedDuplicates: number;
}): boolean {
  return (
    outcome.failedCount > 0 &&
    outcome.failedDocsLength > 0 &&
    (outcome.createdCount > 0 || outcome.skippedDuplicates > 0)
  );
}

export type TruncateFilesResult = {
  files: File[];
  truncated: boolean;
  truncateReason?: 'count' | 'size';
};

/** Apply the same count + total-byte caps used by the directory walker. */
export function truncateFilesByImportCaps(
  files: File[],
  maxFiles: number,
  maxTotalBytes: number
): TruncateFilesResult {
  const out: File[] = [];
  let totalBytes = 0;
  let truncated = false;
  let truncateReason: 'count' | 'size' | undefined;

  for (const file of files) {
    if (out.length >= maxFiles) {
      truncated = true;
      truncateReason = 'count';
      break;
    }
    const size = typeof file.size === 'number' && Number.isFinite(file.size) ? file.size : 0;
    if (totalBytes + size > maxTotalBytes) {
      truncated = true;
      truncateReason = 'size';
      break;
    }
    out.push(file);
    totalBytes += size;
  }

  return { files: out, truncated, truncateReason };
}
